import { ConfigState } from './config.reducer';
import { createSelector, createFeatureSelector } from '@ngrx/store';
import { ConfigApiErrorTypes } from './config.errors';
import { Dictionary } from 'src/app/project-config/components/project-edit-form/models/dictionary.model';
import { orderBy } from 'lodash-es';
import { TtsLanguageOption } from 'src/app/project-config/components/project-edit-form/components/tts-select/models/language-option.model';

/**
 * Feature Selector
 */

export const getConfigState = createFeatureSelector<ConfigState>('config');

/**
 * Primary Selectors
 */

export const getSelectedAvatar = createSelector(
  getConfigState,
  (state: ConfigState) => state.selectedAvatar || (state.current && state.current.values.avatar),
);

export const getCurrentConfig = createSelector(
  getConfigState,
  (state: ConfigState) => state.current,
);

export const getConfigRequestStatus = createSelector(
  getConfigState,
  (state: ConfigState) => state.requestInProgress,
);

export const getConfigRequestError = createSelector(
  getConfigState,
  (state: ConfigState) => state.error,
);

export const getNavBarConfigRequestError = createSelector(getConfigRequestError, (error) =>
  error?.type !== ConfigApiErrorTypes.ConfigConflictError ? error : null,
);

export const getConfigConflictRequestError = createSelector(getConfigRequestError, (error) =>
  error?.type === ConfigApiErrorTypes.ConfigConflictError ? error : null,
);

export const getConfigSchema = createSelector(getConfigState, (state: ConfigState) => state.schema);

/**
 * Derived selectors
 */

// returns the schema for the currently selected avatar
export const getSelectedAvatarSchema = createSelector(
  getSelectedAvatar,
  getConfigSchema,
  (selectedAvatar, configSchema) => {
    let avatarSchema;
    try {
      const allAvatarSchemas = configSchema.definitions.concreteAvatar.oneOf;
      avatarSchema = allAvatarSchemas.find((schema) =>
        schema.allOf[1].properties.avatar.enum.includes(selectedAvatar),
      );
    } catch (e) {
      // we expect this to sometimes fail because either the config
      // or the schema has not been loaded yet.
    }

    return avatarSchema;
  },
);

export const getSelectedAvatarProperties = createSelector(
  getSelectedAvatarSchema,
  (selectedAvatarSchema) => selectedAvatarSchema && selectedAvatarSchema.allOf[0].properties,
);

export const getSelectedAvatarAESAvailability = createSelector(
  getSelectedAvatarSchema,
  (selectedAvatarSchema) => Boolean(selectedAvatarSchema?.allOf[0].properties.disableAES),
);

export const getSelectedAvatarSkillsAvailability = createSelector(
  getSelectedAvatarSchema,
  (selectedAvatarSchema) => Boolean(selectedAvatarSchema?.allOf[0].properties.skills),
);

export const getSelectedAvatarCorpusAvailable = createSelector(
  getSelectedAvatarSchema,
  (selectedAvatarSchema) => selectedAvatarSchema?.allOf[0].properties.corpus?.oneOf,
);

export const getSelectedAvatarRealTimeGesturingAvailability = createSelector(
  getSelectedAvatarSchema,
  (selectedAvatarSchema) => Boolean(selectedAvatarSchema?.allOf[0].properties.realTimeGesturing),
);

export const getSelectedAvatarRealTimeGesturingLocales = createSelector(
  getSelectedAvatarSchema,
  // `realTimeGesturing` is only defined for humanOS2.2+,
  // `meta` might only be defined for humanOS2.3+
  (selectedAvatarSchema) =>
    selectedAvatarSchema?.allOf[0].properties.realTimeGesturing?.meta?.locales ?? [],
);

export const getSelectedAvatarBehaviorStyleAvailability = createSelector(
  getSelectedAvatarSchema,
  (selectedAvatarSchema) =>
    // Behavior styles are only available with real-time gesturing
    Boolean(selectedAvatarSchema?.allOf[0].properties.realTimeGesturing) &&
    Boolean(selectedAvatarSchema?.allOf[0].properties.behaviorStyle),
);

export const getSelectedAvatarReactToNegativeUserExpressionAvailability = createSelector(
  getSelectedAvatarSchema,
  (selectedAvatarSchema) =>
    Boolean(selectedAvatarSchema?.allOf[0].properties.reactToNegativeUserExpression),
);

export const getSelectedAvatarAllowSpeechInterruptionAvailability = createSelector(
  getSelectedAvatarSchema,
  (selectedAvatarSchema) =>
    Boolean(selectedAvatarSchema?.allOf[0].properties.allowSpeechInterruption),
);

export const getSelectedAvatarIconicsPlacementAvailability = createSelector(
  getSelectedAvatarSchema,
  (selectedAvatarSchema) =>
    Boolean(selectedAvatarSchema?.allOf[0].properties.automaticIconicsPlacement),
);

export const getSelectedAvatarReactToNegativeUserSpeechAvailability = createSelector(
  getSelectedAvatarSchema,
  (selectedAvatarSchema) =>
    Boolean(selectedAvatarSchema?.allOf[0].properties.reactToNegativeUserSpeech),
);

export const getSelectedAvatarUserSpeechEmotionAnalysisAvailability = createSelector(
  getSelectedAvatarSchema,
  (selectedAvatarSchema) =>
    Boolean(selectedAvatarSchema?.allOf[0].properties.userSpeechEmotionAnalysis),
);

export const getBehaviorStyleSchema = createSelector(
  getSelectedAvatarRealTimeGesturingAvailability,
  getSelectedAvatarSchema,
  (rtg, selectedAvatarSchema) =>
    rtg ? selectedAvatarSchema?.allOf[0].properties.behaviorStyle : undefined,
);

export const getEnableAESControl = createSelector(
  getSelectedAvatarAESAvailability,
  getSelectedAvatarRealTimeGesturingAvailability,
  (aes, realTime) => aes && !realTime,
);

export const getAvatarEMLDictionaries = createSelector(
  getSelectedAvatarProperties,
  (properties): Dictionary[] => {
    if (!properties || !properties.emlDictionaries) {
      return [];
    }

    // map the id and displayNames to a format which is more
    // usable for the frontend.
    // "id" array determines the order
    const ids = properties.emlDictionaries.meta.description.id as string[];
    const displayNames = properties.emlDictionaries.meta.description.displayName;
    return ids.map((id) => ({ id, displayName: displayNames[id] }));
  },
);

export const getAvatarSentenceDictionaries = createSelector(
  getAvatarEMLDictionaries,
  (dictionaries) => dictionaries && dictionaries.filter((dict) => dict.id.endsWith('Long')),
);

export const getAvatarWordDictionaries = createSelector(
  getAvatarEMLDictionaries,
  (dictionaries) => dictionaries && dictionaries.filter((dict) => dict.id.endsWith('Short')),
);

export const getTtsSchema = createSelector(
  getSelectedAvatarSchema,
  (selectedAvatarSchema) => selectedAvatarSchema?.allOf[0].properties.tts,
);

export const getTtsLanguageOptions = createSelector(
  getTtsSchema,
  (ttsSchema): Array<{ displayName: string; languageCodes: string[] }> => {
    const languageOptions = ttsSchema?.meta?.description?.language;

    if (!languageOptions) {
      return [];
    }

    if (Array.isArray(languageOptions)) {
      return orderBy(languageOptions, 'displayName', 'asc');
    }

    // re-map to an array because the dictionary approach is deprecated.
    const transformedLanguageOptions: TtsLanguageOption[] = [];

    Object.entries(languageOptions).forEach(([languageCode, displayName]) => {
      const existingLanguageEntry = transformedLanguageOptions.find(
        (entry) => entry.displayName === displayName,
      );
      if (existingLanguageEntry) {
        // add the language code to the existing entry
        existingLanguageEntry.languageCodes.push(languageCode);
      } else {
        // create a new entry for the language
        transformedLanguageOptions.push({
          displayName: displayName as string,
          languageCodes: [languageCode],
        });
      }
    });

    return orderBy(transformedLanguageOptions, 'displayName', 'asc');
  },
);
